import * as React from "react";

import { cn } from "utils/cn";
import { useFormFieldControl } from "./FormField";

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called once when the last digit is entered (typed, pasted or autofilled). */
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  id?: string;
  className?: string;
  "aria-describedby"?: string;
}

/**
 * One-time code entry. A single real <input> sits on top of the visual cells, so SMS/WhatsApp
 * autofill (`one-time-code`), pasting a whole message and backspace all behave natively.
 */
export const OtpInput = React.forwardRef<HTMLInputElement, OtpInputProps>(
  (
    {
      value,
      onChange,
      onComplete,
      length = 6,
      disabled = false,
      invalid = false,
      autoFocus = false,
      className,
      ...rest
    },
    ref
  ) => {
    const fieldProps = useFormFieldControl({
      id: rest.id,
      "aria-describedby": rest["aria-describedby"],
      "aria-invalid": invalid || undefined,
    });
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [focused, setFocused] = React.useState(false);

    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const keepCaretAtEnd = () => {
      const node = inputRef.current;
      if (node) {
        node.setSelectionRange(node.value.length, node.value.length);
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const digits = event.target.value.replace(/\D/g, "").slice(0, length);
      onChange(digits);
      if (digits.length === length && value.length !== length) {
        onComplete?.(digits);
      }
    };

    const isInvalid = invalid || fieldProps["aria-invalid"] === true;
    const activeIndex = Math.min(value.length, length - 1);
    const groupBreak = length === 6 ? 3 : -1;

    return (
      <div data-ui="otp-input" className={cn("relative inline-flex", className)}>
        <div aria-hidden="true" className="flex items-center gap-2">
          {Array.from({ length }, (_, index) => {
            const char = value[index];
            const isActive = focused && index === activeIndex && !disabled;
            return (
              <React.Fragment key={index}>
                {index === groupBreak && <span className="h-px w-3 bg-input" />}
                <div
                  className={cn(
                    "relative flex h-12 w-11 items-center justify-center rounded-control border bg-surface font-mono text-xl font-medium text-foreground shadow-xs",
                    "transition-[border-color,box-shadow] duration-fast ease-standard",
                    isInvalid ? "border-danger" : "border-input",
                    isActive && !isInvalid && "border-primary ring-4 ring-ring/15",
                    isActive && isInvalid && "ring-4 ring-danger/15",
                    disabled && "bg-surface-muted opacity-70"
                  )}
                >
                  {char}
                  {isActive && !char && (
                    <span className="h-5 w-px animate-caret-blink bg-foreground motion-reduce:animate-none" />
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <input
          ref={setRefs}
          {...fieldProps}
          value={value}
          onChange={handleChange}
          onFocus={() => {
            setFocused(true);
            keepCaretAtEnd();
          }}
          onBlur={() => setFocused(false)}
          onSelect={keepCaretAtEnd}
          disabled={disabled}
          autoFocus={autoFocus}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={length}
          aria-label={fieldProps.id ? undefined : `Verification code, ${length} digits`}
          spellCheck={false}
          className="absolute inset-0 h-full w-full cursor-text bg-transparent text-transparent caret-transparent opacity-0 outline-none selection:bg-transparent"
        />
      </div>
    );
  }
);
OtpInput.displayName = "OtpInput";
